import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, AlertTriangle, Building2 } from "lucide-react";
import { SegmentedControl } from "../primitives";
import { SourcePill, EmptyState, Skeleton, fmtDate, plural } from "./ui";

// Dense, filterable view of every member lab and where its live test menu
// comes from. Filtering is client-side — a chain's member list is small.
const MemberLabsTable = ({ labs, lists, loading }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const options = useMemo(() => [
    { value: "all", label: `All ${labs.length}` },
    { value: "chain", label: `Chain list ${labs.filter((l) => l.test_source === "chain").length}` }, // contract: chain_test_list_serializers.py serialize_chain_member_labs test_source
    { value: "direct", label: `Direct ${labs.filter((l) => l.test_source === "direct").length}` }, // contract: chain_test_list_serializers.py serialize_chain_member_labs test_source
    ...lists.map((l) => ({ value: l.id, label: l.name })),
  ], [labs, lists]);

  const q = query.trim().toLowerCase();
  const rows = useMemo(() => labs.filter((lab) => { // contract: chain_test_list_serializers.py serialize_chain_member_labs test_source / test_list
    if (filter === "chain" && lab.test_source !== "chain") return false;
    if (filter === "direct" && lab.test_source !== "direct") return false;
    if (!["all", "chain", "direct"].includes(filter) && lab.test_list?.id !== filter) return false;
    return !q || `${lab.brand_name} ${lab.city || ""} ${lab.code || ""}`.toLowerCase().includes(q);
  }), [labs, filter, q]);

  if (loading) {
    return (
      <div className="p-5 space-y-3">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
      </div>
    );
  }
  if (labs.length === 0) {
    return (
      <EmptyState icon={Building2} title="No member labs yet"
        description="Add labs to this chain from the chain page, then assign them a test list here." />
    );
  }

  return (
    <div>
      <div className="px-5 py-3 border-b border-[#E9F2F2] flex flex-col gap-2.5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search labs, city or code…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-[#00A6A6]/50 focus:border-transparent" />
        </div>
        <div className="overflow-x-auto -mx-1 px-1">
          <SegmentedControl size="sm" options={options} value={filter} onChange={setFilter} />
        </div>
      </div>

      <div className="hidden md:grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.2fr)_90px_24px] gap-3 px-5 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400 border-b border-gray-50">
        <span>Lab</span><span>Test list source</span><span className="text-right">Tests</span><span />
      </div>

      {rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-gray-400">No labs match these filters.</p>
      ) : (
        <ul className="divide-y divide-gray-50 max-h-[62vh] overflow-y-auto">
          {rows.map((lab) => {
            const empty = lab.test_count === 0;
            return (
              <li key={lab.id}>
                <button type="button" onClick={() => navigate(`/subscriber-wallet/labs/labs/${lab.id}`)}
                  className="w-full grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1.6fr)_minmax(0,1.2fr)_90px_24px] items-center gap-3 px-5 py-3 text-left hover:bg-[#F7FBFB] transition-colors">
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{lab.brand_name}</span>
                      {!lab.active && <span className="text-[10px] font-medium uppercase text-gray-400">Inactive</span>}
                    </span>
                    <span className="block text-[11px] text-gray-400 truncate">{[lab.city, lab.code].filter(Boolean).join(" · ") || "—"}</span>
                  </span>
                  <span className="hidden md:block min-w-0">
                    <SourcePill lab={lab} />
                    {lab.test_source === "direct" && lab.direct_uploaded_at && (
                      <span className="block mt-0.5 text-[10px] text-gray-400">Uploaded {fmtDate(lab.direct_uploaded_at)}</span>
                    )}
                  </span>
                  <span className={`text-right text-sm tabular-nums ${empty ? "text-amber-600 font-medium" : "text-gray-700"}`}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {empty && <AlertTriangle className="w-3.5 h-3.5" />}
                      <span className="md:hidden text-[11px] text-gray-400 mr-1">{lab.test_source === "chain" ? lab.test_list?.name : "Direct"} ·</span>
                      {lab.test_count.toLocaleString("en-IN")}
                    </span>
                  </span>
                  <ChevronRight className="hidden md:block w-4 h-4 text-gray-300" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="px-5 py-2.5 border-t border-[#E9F2F2] text-[11px] text-gray-400">
        Showing {plural(rows.length, "lab")} · a lab with 0 tests can't be booked
      </p>
    </div>
  );
};

export default MemberLabsTable;
